import { FC, useState, useEffect } from 'react';
import { Card, Table, Tag, Empty, Button, Space } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  NSDCampEnrollment,
  CampDayEnrollment,
} from '../../../../../models/admin/student-admin.model';

interface NSDCampsTabProps {
  enrollments: NSDCampEnrollment[];
  studentId: number;
}

const NSDCampsTab: FC<NSDCampsTabProps> = ({ enrollments, studentId }) => {
  const [isCampAdmin, setIsCampAdmin] = useState<boolean>(false);

  // Check if user has Camp Admin access (accessContextId = 2) or Web Admin (accessContextId = 3)
  useEffect(() => {
    const checkCampAdminContext = () => {
      const auth = localStorage.getItem('auth');
      if (auth) {
        try {
          const authObj = JSON.parse(auth);
          // Camp Admin context = 2, Web Admin context = 3 (both can manage camps)
          const accessContextId = authObj.selectedAccessContextId;
          setIsCampAdmin(accessContextId === 2 || accessContextId === 3);
        } catch {
          setIsCampAdmin(false);
        }
      }
    };
    checkCampAdminContext();
  }, []);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const renderEnrolledDays = (days: CampDayEnrollment[]) => {
    if (!days || days.length === 0) {
      return <span>-</span>;
    }

    return (
      <Space wrap>
        {days.map((day) => (
          <Tag key={day.id} color={day.enrolled ? 'green' : 'default'}>
            {day.dayName}
          </Tag>
        ))}
      </Space>
    );
  };

  const columns = [
    {
      title: 'Camp',
      dataIndex: 'campName',
      key: 'campName',
    },
    {
      title: 'Date',
      dataIndex: 'campDate',
      key: 'campDate',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Enrolled Days',
      dataIndex: 'days',
      key: 'days',
      render: (days: CampDayEnrollment[]) => renderEnrolledDays(days),
    },
  ];

  return (
    <div>
      {isCampAdmin && (
        <div style={{ marginBottom: 16, textAlign: 'right' }}>
          <Link to={`/admin/students/${studentId}/camp-schedule-change`}>
            <Button type="primary" icon={<CalendarOutlined />}>
              Camp Schedule Change
            </Button>
          </Link>
        </div>
      )}

      <Card title="NSD Camp Enrollments">
        {enrollments.length > 0 ? (
          <Table
            dataSource={enrollments}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="middle"
          />
        ) : (
          <Empty description="No camp enrollments" />
        )}
      </Card>
    </div>
  );
};

export default NSDCampsTab;
